import CommentCountBadge from "components/CommentCountBadge";
import ElementLoader from "components/ElementLoader";
import { IconLike } from "components/Icon/Icon";
import { ImageIconComponent } from "components/ImageIcon/ImageIcon";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import { FeRecordType } from "config/config";
import { CSSProperties, ReactNode, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { isContainerNavigationLinkActive } from "utils/RouterManager/subPageLinks";
import { useIsVisible } from "utils/useIsVisible";
import { getLeadingSpaces, getTrailingSpaces } from "utils/utils";
import "./RecordLabel.scss";

interface RecordLabelProps {
    image?: string | null;
    icon?: IconLike;
    iconByType?: FeRecordType;
    link?: string | null;
    name?: string;
    description?: string | null;
    loading?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    contentStyle?: React.CSSProperties;
    hasOverlappingTooltip?: boolean;
    /**
     * Opt-in border-aware placement for the standard (non-overlapping) truncation
     * tooltip — keeps the caret pointing at the label when it sits near the
     * viewport's left/right edge (e.g. nodes at the border of a zoomable canvas).
     * Only meaningful when hasOverlappingTooltip is false. See OverflowTooltip.
     */
    edgeAwarePlacement?: boolean;
    linkWithNameAttr?: string;
    chip?: React.ReactNode;
    /**
     * Total number of comments on the record. When > 0, an inert comment-count
     * indicator is rendered inline to the right of the name (0/undefined → no
     * indicator), so every record-name link shows at a glance whether it has
     * discussion. (operator-task-fe-record-comment-indicator-on-links)
     */
    commentCount?: number | null;
    /**
     * Inert state-badge indicator(s) (read-only / bundle-content / changed / …),
     * rendered inline on the name line right after the name — the SAME hugging
     * placement as the comment-count indicator, never dropped to a clipped second
     * row (Margot, operator-task-fe-list-view-row-height-harmonize-real). Pass a
     * single badge or a <StateBadges> strip. For table name cells the equivalent
     * placement is achieved with the external `.nameCellWithBadges` flex wrapper;
     * this prop is for surfaces that render a bare RecordLabel (e.g. the left-nav
     * bundle tree, where the old `chip` prop stacked the badge below the name).
     */
    stateBadges?: ReactNode;
    tooltipOverlayStyle?: CSSProperties;
    tooltipOverlay?: ReactNode;
    matchLocationPath?: boolean;
    scrollIntoView?: boolean;
    tooltipOverlayClassName?: string;
    imageComponent?: React.ReactElement;
    isSelectedManually?: boolean;
    /**
     * Skip the overflow tooltip on the name. For callers that wrap the label in
     * their own always-on tooltip, so two popups do not stack.
     */
    disableOverflowTooltip?: boolean;
}

const RecordLabel = ({
    link,
    name,
    description,
    image,
    icon,
    iconByType,
    loading,
    onClick,
    children,
    contentStyle,
    hasOverlappingTooltip = true,
    edgeAwarePlacement = false,
    linkWithNameAttr,
    chip,
    commentCount,
    stateBadges,
    tooltipOverlayStyle,
    tooltipOverlay,
    matchLocationPath,
    scrollIntoView,
    tooltipOverlayClassName,
    imageComponent,
    isSelectedManually,
    disableOverflowTooltip = false,
}: RecordLabelProps) => {
    const linkRef = useRef<HTMLDivElement>(null);

    const getTooltipOverlay = () => {
        if (hasOverlappingTooltip) {
            if (tooltipOverlay) {
                return tooltipOverlay;
            } else if (link) {
                return (
                    <NavLink to={link} onClick={onClick}>
                        {name}
                    </NavLink>
                );
            } else {
                return name;
            }
        }
        return name;
    };

    const nameText = (
        <span className="text">
            <span className="space-background">{getLeadingSpaces(name)}</span>
            {name?.trim()}
            <span className="space-background">{getTrailingSpaces(name)}</span>
        </span>
    );

    const content = (
        <>
            {loading && <ElementLoader />}
            <ImageIconComponent
                image={image}
                iconByType={iconByType}
                icon={icon}
                imageComponent={imageComponent}
            />
            <div className="textLines" style={contentStyle}>
                {disableOverflowTooltip ? (
                    nameText
                ) : (
                    <OverflowTooltip
                        overlay={getTooltipOverlay()}
                        isOverlapping={hasOverlappingTooltip}
                        edgeAwarePlacement={edgeAwarePlacement}
                        overlayStyle={tooltipOverlayStyle}
                        overlayClassName={tooltipOverlayClassName}
                    >
                        {nameText}
                    </OverflowTooltip>
                )}
                {description && <small className="text-dark-grey description">{description}</small>}
                {chip}
            </div>
            {stateBadges && <span className="recordLabelStateBadges">{stateBadges}</span>}
            <CommentCountBadge count={commentCount} className="recordLabelCommentBadge" />
        </>
    );

    const location = useLocation();
    const currentPathname = location.pathname;
    const currentSearch = location.search;

    // Match query-param links (e.g. /flow?id=<uuid>) against path-based sub-page URLs (e.g. /flow/<uuid>/logs)
    const linkIdMatch = link?.match(/^(\/[^?]+)\?id=([^&]+)/);
    const isPathBasedView = !!(
        linkIdMatch &&
        (currentPathname === `${linkIdMatch[1]}/${linkIdMatch[2]}` ||
            currentPathname.startsWith(`${linkIdMatch[1]}/${linkIdMatch[2]}/`))
    );

    const isLinkActive = (targetLink: string) =>
        isContainerNavigationLinkActive(targetLink, currentPathname, currentSearch);

    const isSelected =
        isSelectedManually ||
        (link && isLinkActive(link)) ||
        (linkWithNameAttr && isLinkActive(linkWithNameAttr)) ||
        (matchLocationPath && link?.includes(currentPathname)) ||
        isPathBasedView;

    const isVisible = useIsVisible(linkRef);

    const scrollToView = (ref) => {
        // 300ms > react-pro-sidebar slidedown animation (200ms), so the element is at its
        // final position in the DOM when scrollIntoView fires.
        setTimeout(function () {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
    };

    useEffect(() => {
        if (scrollIntoView && isSelected && linkRef.current && !isVisible) {
            scrollToView(linkRef);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, scrollIntoView]);

    if (link) {
        return (
            <div className={`record-label ${isSelected ? "selected" : ""}`} ref={linkRef}>
                <NavLink
                    to={link}
                    onClick={onClick}
                    className="label"
                    isActive={() => isSelected}
                    activeClassName="selected"
                >
                    {content}
                </NavLink>
                <div className="recordLabelChildren">{children}</div>
            </div>
        );
    }

    return (
        <div className={`record-label ${isSelected ? "selected" : ""}`} onClick={onClick}>
            <div className="label">{content}</div>
            <div className="recordLabelChildren">{children}</div>
        </div>
    );
};

export default RecordLabel;
